import { Option, QuestionDto } from '@smatchy/sdk';

export type AnswerValueType = string | number;

export class SurveyEntity {
  private _historicAnswers = new Map<string, AnswerValueType>();
  private _historicQuestions = new Map<string, QuestionDto>();
  private _surveyEnded = false;
  private _currLevel = 1;
  private _countNbToBeAnswered = 1;
  private _currentQuestion: QuestionDto;
  private _nextQuestion: QuestionDto;

  private constructor(readonly props: QuestionDto) {
    this._currentQuestion = props;
    // `?? 0` before the addition: `undefined + 1` is NaN, and `NaN || 1`
    // happened to answer 1 -- which is the right number, reached by accident
    // rather than by saying a question with no sub-questions counts as one.
    this._countNbToBeAnswered = (props?.nesteds?.length ?? 0) + 1;
    this._historicQuestions.set(props.id, props);
  }

  get totalToAnswer(): number {
    return this._countNbToBeAnswered;
  }

  static create(props: QuestionDto) {
    return new SurveyEntity(props);
  }

  answerQuestion(value: AnswerValueType, question: QuestionDto) {
    this._historicQuestions.set(question.id, question);
    if (this._historicAnswers.has(question.id)) {
      this._historicAnswers.set(question.id, value);
    } else {
      this._historicAnswers.set(question.id, value);
      // Numbers, floats and times carry their answer directly and have no option
      // to follow, so only the other types look one up.
      if (question.type !== 'number' && question.type !== 'float' && question.type !== 'time') {
        const optionFound = question.options.find((o) => o.value.toString() === value.toString());
        if (optionFound) {
          if (optionFound.scoreLevel) {
            this._currLevel = optionFound.scoreLevel;
          }
          if (optionFound.nested) {
            this._countNbToBeAnswered += 1;
            this._currentQuestion = optionFound.nested;
          } else {
            // A multi-choice question stays open until the person is done, so only
            // the single-answer types end the survey here.
            if (question.type !== 'multi') {
              this._surveyEnded = true;
            }
          }
        }
      }
    }
  }

  isFirstQuestion() {
    return this.props.id === this._currentQuestion.id;
  }

  isOver() {
    return this._surveyEnded;
  }

  getLevel() {
    return this._currLevel;
  }

  getCurrentQuestion() {
    return this._currentQuestion;
  }

  allQuestionOfMultiAnswered(question: QuestionDto): boolean {
    if (question.type === 'multi') {
      // `?? []`: a multi question with no sub-questions has none answered,
      // and `every` over nothing is true, which is what an empty branch means.
      const questionIds = (question.nesteds ?? []).map((n) => n.id);
      return questionIds.every((q) => this._historicAnswers.has(q));
    }
    return false;
  }

  getAnswer(question: QuestionDto) {
    return this._historicAnswers.get(question.id) || undefined;
  }

  getAverageScoreAnswers(): number {
    let totalScore = 0;
    let answeredQuestions = 0;

    this._historicAnswers.forEach((value, questionId) => {
      const question = this._historicQuestions.get(questionId);
      if (question) {
        const option = this.findSelectedOption(question, value);
        if (option && option.scoreLevel !== undefined) {
          totalScore += option.scoreLevel;
          answeredQuestions++;
        }
      }
    });

    return answeredQuestions > 0 ? Math.round(totalScore / answeredQuestions) : 0;
  }

  getFirstQuestion() {
    return this.props;
  }

  validate() {
    this._surveyEnded = true;
  }

  validateAndPassQuestions() {
    this._currLevel = 1;
    this.validate();
  }

  getPercent(maxLevel: number) {
    return (this.getLevel() / (maxLevel || 1)) * 100;
  }

  setLevel(level: number) {
    this._currLevel = level;
  }

  reset() {
    this._surveyEnded = false;
  }

  private findSelectedOption(question: QuestionDto, value: AnswerValueType): Option | undefined {
    if (question.type === 'multi') {
      // Pour les questions multi, nous devons vérifier si la valeur est un tableau
      if (Array.isArray(value)) {
        // Calculer la moyenne des scores pour toutes les options sélectionnées
        const selectedOptions = question.options.filter((o) => value.includes(o.value));
        if (selectedOptions.length > 0) {
          const totalScore = selectedOptions.reduce((sum, option) => sum + (option.scoreLevel || 0), 0);
          return { scoreLevel: totalScore / selectedOptions.length } as Option;
        }
      }
    } else {
      return question.options.find((o) => o.value.toString() === value);
    }
    return undefined;
  }
}
